
import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ProcurementFormValues } from "../schema";
import { useGetFarms } from "@/hooks/use-farm";
import { useGetSuppliers } from "@/hooks/use-supplier";
import { useGetAgents } from "@/hooks/use-agent";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

interface SourceFieldProps {
  form: UseFormReturn<ProcurementFormValues>;
}

export const SourceField = ({ form }: SourceFieldProps) => {
  const { data: farms, isLoading: isLoadingFarms } = useGetFarms();
  const { data: suppliers, isLoading: isLoadingSuppliers } = useGetSuppliers();
  const { data: agents, isLoading: isLoadingAgents } = useGetAgents();
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [farmOpen, setFarmOpen] = useState(false);
  const [agentOpen, setAgentOpen] = useState(false);

  const sourceType = form.watch("source_type");

  return (
    <div className="space-y-4">
      <FormField
        control={form.control}
        name="source_type"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Source Type</FormLabel>
            <FormControl>
              <RadioGroup
                onValueChange={field.onChange}
                defaultValue={field.value}
                className="flex flex-col space-y-1"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="farm" id="farm" />
                  <label htmlFor="farm">Farm</label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="supplier" id="supplier" />
                  <label htmlFor="supplier">Supplier</label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="agent" id="agent" />
                  <label htmlFor="agent">Agent</label>
                </div>
              </RadioGroup>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {sourceType && (
        <FormField
          control={form.control}
          name="source_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Select {sourceType}</FormLabel>
              
              {sourceType === "supplier" && (
                <Popover open={supplierOpen} onOpenChange={setSupplierOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={supplierOpen}
                        className={cn(
                          "w-full justify-between",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value
                          ? suppliers?.find((supplier) => supplier.id === field.value)?.name || "Select a supplier..."
                          : "Select a supplier..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search suppliers..." className="h-9" />
                      <CommandList>
                        <CommandEmpty>No suppliers found.</CommandEmpty>
                        <CommandGroup>
                          {suppliers?.map((supplier) => (
                            <CommandItem
                              key={supplier.id}
                              value={supplier.name}
                              onSelect={() => {
                                field.onChange(supplier.id === field.value ? "" : supplier.id);
                                setSupplierOpen(false);
                              }}
                            >
                              {supplier.name}
                              <Check
                                className={cn(
                                  "ml-auto h-4 w-4",
                                  field.value === supplier.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}

              {sourceType === "farm" && (
                <Popover open={farmOpen} onOpenChange={setFarmOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={farmOpen}
                        className={cn(
                          "w-full justify-between",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value
                          ? farms?.find((farm) => farm.id === field.value)?.farmer_name || "Select a farm..."
                          : "Select a farm..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search farms..." className="h-9" />
                      <CommandList>
                        <CommandEmpty>No farms found.</CommandEmpty>
                        <CommandGroup>
                          {farms?.map((farm) => (
                            <CommandItem
                              key={farm.id}
                              value={farm.farmer_name}
                              onSelect={() => {
                                field.onChange(farm.id === field.value ? "" : farm.id);
                                setFarmOpen(false);
                              }}
                            >
                              {farm.farmer_name}
                              <Check
                                className={cn(
                                  "ml-auto h-4 w-4",
                                  field.value === farm.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}

              {sourceType === "agent" && (
                <Popover open={agentOpen} onOpenChange={setAgentOpen}>
                  <PopoverTrigger asChild>
                    <FormControl>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={agentOpen}
                        className={cn(
                          "w-full justify-between",
                          !field.value && "text-muted-foreground"
                        )}
                      >
                        {field.value
                          ? agents?.find((agent) => agent.id === field.value)?.name || "Select an agent..."
                          : "Select an agent..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </FormControl>
                  </PopoverTrigger>
                  <PopoverContent className="w-full p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search agents..." className="h-9" />
                      <CommandList>
                        <CommandEmpty>No agents found.</CommandEmpty>
                        <CommandGroup>
                          {agents?.map((agent) => (
                            <CommandItem
                              key={agent.id}
                              value={agent.name}
                              onSelect={() => {
                                field.onChange(agent.id === field.value ? "" : agent.id);
                                setAgentOpen(false);
                              }}
                            >
                              {agent.name}
                              <Check
                                className={cn(
                                  "ml-auto h-4 w-4",
                                  field.value === agent.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}

              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
};
